"use client";

import { type CSSProperties, type HTMLAttributes } from "react";
import {
  motion,
  type DOMMotionComponents,
  type MotionProps,
} from "motion/react";
import { cn } from "@/lib/utils";
import styles from "./line-shadow-text.module.css";

const motionElements = {
  article: motion.article,
  div: motion.div,
  h1: motion.h1,
  h2: motion.h2,
  h3: motion.h3,
  h4: motion.h4,
  h5: motion.h5,
  h6: motion.h6,
  li: motion.li,
  p: motion.p,
  section: motion.section,
  span: motion.span,
} as const;

type MotionElementType = Extract<
  keyof DOMMotionComponents,
  keyof typeof motionElements
>;

type LineShadowTextProps = Omit<HTMLAttributes<HTMLElement>, keyof MotionProps> &
  MotionProps & {
    children: string;
    shadowColor?: string;
    as?: MotionElementType;
  };

/**
 * Magic UI line-shadow-text.
 * Hatch is a sibling layer behind an opaque face (not ::after -z-10), so scroll-spine
 * opacity utilities like `.dark_30` cannot make the shadow read as “in front”.
 */
export function LineShadowText({
  children,
  shadowColor = "black",
  className,
  as: Component = "span",
  style,
  ...props
}: LineShadowTextProps) {
  const MotionComponent = motionElements[Component];

  return (
    <MotionComponent
      style={
        {
          ["--shadow-color" as string]: shadowColor,
          ...style,
        } as CSSProperties
      }
      className={cn(styles.root, className)}
      data-text={children}
      {...props}
    >
      <span className={styles.shadow} aria-hidden="true">
        {children}
      </span>
      <span className={styles.face}>{children}</span>
    </MotionComponent>
  );
}
